import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react'
import {useMeasure} from 'react-use'
import _ from 'lodash'
import Fuse from 'fuse.js'
import {useGesture} from '@use-gesture/react'
import {Button, Checkbox, Icon, Input, Popup} from 'semantic-ui-react'
import {Align} from 'react-window'
import {languages} from 'monaco-editor'
import {PiListBold} from 'react-icons/pi'
import {TbCropPortraitFilled} from 'react-icons/tb'
import {exportAsFile} from '@/utils/files'
import {useNavState} from '@/features/nav-state'
import {Split, SplitProps} from '@/features/split'
import {DataSourcesContext, IDataSourceItem} from '@/features/data-sources'
import {
  defaultAutoCompleteSuggestions,
  ExpressionEditorAutoCompleteContext,
  ExpressionEditorAutoCompleteProviderArgs,
  fieldStatsToAutoCompleteSuggestions,
} from '@/features/expressions'
import {SettingsPopup} from '@/components/settings-popup'
import {ToolBar} from '../tool-bar'
import {
  IMediaListRef,
  IMixerChannel,
  IMixerConfig,
  IMixerState,
  IMixerUrlParams,
  defaultNavState,
  language,
  MediaList,
  MediaListStats,
  SemanticFilteringItemType,
} from '../..'
import {Channels} from './channels'
import './style.css'

export interface IMixerSidebarProps extends SplitProps {
  compact?: boolean
  disableSetSearchParams?: boolean
  disableSetSelectedSeries?: boolean
  config?: Partial<IMixerConfig>
  itemFilter?: (item: SemanticFilteringItemType) => boolean
  itemOverlay?: (item: SemanticFilteringItemType) => React.ReactNode
  // selectedItem?: SemanticFilteringItemType,
  onSelectItem?: (item: SemanticFilteringItemType | undefined) => void
  disableOnItemClick?: boolean
}

export interface IMixerSidebarRef<T extends IDataSourceItem> {
  config: IMixerConfig
  channelKeys: string[]
  scrollToItem?: (item: T, align?: Align) => void
  selectPreviousItem: () => void
  selectNextItem: () => void
  children?: React.ReactNode
  // mediaListRef: React.RefObject<IMediaListRef<SemanticFilteringItemType>>,
}

export const Sidebar = forwardRef<IMixerSidebarRef<SemanticFilteringItemType>, IMixerSidebarProps>(
  (props, ref) => {
    const {
      disableSetSearchParams,
      disableSetSelectedSeries,
      compact: _propsCompact,
      config: _propsConfig,
      itemFilter,
      itemOverlay,
      // selectedItem,
      onSelectItem,
      disableOnItemClick,
      children,
      ...rest
    } = props

    const [measureRef, size] = useMeasure<HTMLDivElement>()

    const mediaListRef = useRef<IMediaListRef<SemanticFilteringItemType>>(null)

    const {mergedItems, mergedItemsStats, findItem, ensureFetch} = useContext(DataSourcesContext)

    const {searchParams, navState, navigate} = useNavState<IMixerState, IMixerUrlParams>()

    useImperativeHandle(ref, () => {
      return {
        config,
        channelKeys,
        scrollToItem: mediaListRef.current?.scrollToItem,
        selectPreviousItem,
        selectNextItem,
      }
    })

    const [config, setConfig] = useState<IMixerConfig>({
      mediaListViewMode: 'portrait',
      showAdvancedControls: false,
      keepSelectionInView: true,
      nestedEpisodes: true,
      ..._propsConfig,
    })
    const [items, setItems] = useState<SemanticFilteringItemType[]>(mergedItems)
    const [channelKeys, setChannelKeys] = useState<string[]>([])
    const [isCompact, setIsCompact] = useState(_propsCompact || false)
    const [searchQuery, setSearchQuery] = useState<string>('')
    const [filteredItems, setFilteredItems] = useState<SemanticFilteringItemType[]>(items)

    const fuse = useMemo(() => {
      return new Fuse(items, {
        keys: ['title'],
        threshold: 0.2,
        includeScore: true,
      })
    }, [items])

    useEffect(() => {
      if (!searchQuery.trim()) {
        setFilteredItems(items)
      } else {
        const results = fuse.search(searchQuery.trim())
        setFilteredItems(results.map((result) => result.item))
      }
    }, [searchQuery, items, fuse])

    const {channels, selectedItem, selectedSeries} = {
      ...defaultNavState,
      ...navState,
    }

    useEffect(ensureFetch)

    const mergedItemsWithSeries = useMemo(() => {
      const movies: SemanticFilteringItemType[] = []
      const episodes: SemanticFilteringItemType[] = []
      const series: SemanticFilteringItemType[] = []

      for (let i = 0; i < mergedItems.length; i++) {
        const item = {...mergedItems[i], title: mergedItems[i].title?.trim() ?? ''}
        if (item.type === 'movie') movies.push(item)
        else if (item.type === 'episode') episodes.push(item)
        else if (item.type === 'series') series.push(item)
        else movies.push(item)
      }

      movies.sort((a, b) => a.title?.localeCompare(b.title || '') || 0)
      episodes.sort((a, b) => a.title?.localeCompare(b.title || '') || 0)
      series.sort((a, b) => a.title?.localeCompare(b.title || '') || 0)

      const episodesBySeries = _.groupBy(episodes, 'series_id')
      const moviesAndEpisodes = _.concat(movies, episodes)
      const moviesAndSeries = _.concat(movies, series)

      return {
        moviesAndEpisodes,
        moviesAndSeries,
        episodesBySeries,
      }
    }, [mergedItems])

    useEffect(() => {
      if (selectedSeries?.ident && config.nestedEpisodes) {
        setItems([selectedSeries, ...mergedItemsWithSeries.episodesBySeries[selectedSeries.ident]])
      } else {
        setItems(
          config.nestedEpisodes ?
            mergedItemsWithSeries.moviesAndSeries
          : mergedItemsWithSeries.moviesAndEpisodes
        )
      }
    }, [mergedItemsWithSeries, config.nestedEpisodes, selectedItem, selectedSeries])

    useEffect(() => {
      // setIsCompact(size.width < 350);
      if (mediaListRef.current && selectedItem && config.keepSelectionInView)
        mediaListRef.current.scrollToItem(selectedItem)
    }, [size, config.keepSelectionInView])

    useEffect(() => {
      async function f() {
        if (!searchParams?.item) return
        const itemID = _.chain(searchParams.item).split(',').compact().first().value()
        const _item =
          _.find(items, (item) => item._id === itemID) ||
          ((await findItem(searchParams.item)) as SemanticFilteringItemType)
        if (!_item) {
          console.log('item not in catalog', searchParams.item)
          return
        }
        navigate({
          state: {
            selectedItem: _item,
          },
        })
        if (mediaListRef.current && _item && config.keepSelectionInView)
          mediaListRef.current.scrollToItem(_item)
      }
      f()
    }, [searchParams, items, config.keepSelectionInView])

    useEffect(() => {
      setChannelKeys(
        _.chain(channels?.adjust)
          .filter((v) => {
            if (v.value.mute) return false
            return true
            // return (
            //     v.value.sort !== 0
            //     || (
            //         v.value.cut !== undefined
            //         && (
            //             v.value.cut.min !== v.descriptor.range.min
            //             || v.value.cut.max !== v.descriptor.range.max
            //         )
            //     )
            // );
          })
          .map(({descriptor}) => channelToKey(descriptor))
          .compact()
          .value()
      )
    }, [channels?.adjust])

    useEffect(() => {
      if (!mediaListRef.current) return
      if (!selectedItem) return
      if (config.keepSelectionInView) mediaListRef.current.scrollToItem(selectedItem)
    }, [items, config.mediaListViewMode])

    useEffect(() => {
      if (disableSetSelectedSeries && selectedItem?.type === 'series') return
      onSelectItem?.(selectedItem)
    }, [selectedItem, disableSetSelectedSeries])

    useGesture(
      {
        onKeyDown: (e) => handleOnKeyDown(e.event),
      },
      {
        target: window,
      }
    )

    const channelToKey = useCallback(({sourcePath}: IMixerChannel['descriptor']): string => {
      return sourcePath
    }, [])

    const patchConfig = useCallback((change: Partial<IMixerConfig>) => {
      setConfig((prev) => ({
        ...prev,
        ...change,
      }))
    }, [])

    const generateExpressionAutoCompleteSuggestions = useCallback(
      (args: ExpressionEditorAutoCompleteProviderArgs): languages.CompletionItem[] => {
        return [
          ...defaultAutoCompleteSuggestions(args),
          ...fieldStatsToAutoCompleteSuggestions({
            ...args,
            fieldTypes: mergedItemsStats.fieldTypes,
          }),
        ]
      },
      [mergedItemsStats]
    )

    const handleOnMediaListSizeChanged = useCallback(() => {
      if (!selectedItem) return
      if (!mediaListRef.current) return
      if (config.keepSelectionInView) mediaListRef.current.scrollToItem(selectedItem)
    }, [selectedItem, config.keepSelectionInView])

    const handleOnMediaClick = useCallback(
      (media: SemanticFilteringItemType) => {
        const disableParams =
          disableSetSearchParams || (disableSetSelectedSeries && media.type === 'series')
        const isSelected = selectedItem && selectedItem._id === media._id
        const item = isSelected ? undefined : media
        const params = disableParams ? undefined : {searchParams: {item: item?._id}}

        if (selectedItem && selectedItem._id === media._id) {
          navigate({
            ...params,
            state: {
              selectedItem: item,
              selectedSeries:
                (
                  config.nestedEpisodes &&
                  media.type === 'series' &&
                  selectedSeries?._id !== media._id
                ) ?
                  media
                : undefined,
            },
          })
        } else {
          navigate({
            ...params,
            state: {
              selectedItem: item,
              selectedSeries:
                media.type === 'series' && !selectedSeries ? media
                : (
                  media.type === 'episode' &&
                  media.series_id &&
                  selectedSeries?.ident === media.series_id
                ) ?
                  selectedSeries
                : undefined,
            },
          })
        }
      },
      [
        disableSetSearchParams,
        disableSetSelectedSeries,
        selectedItem,
        navigate,
        config.nestedEpisodes,
        selectedSeries,
      ]
    )

    const selectNeighbour = useCallback(
      (step: number) => {
        if (step === 0) return
        const selectedItemIndex = filteredItems.findIndex(({_id}) => _id === selectedItem?._id)
        let i = selectedItemIndex + step
        while (true) {
          if (i < 0) return
          if (i >= filteredItems.length) return
          const data = filteredItems[i]
          if (data && data.hidden !== true) {
            navigate({
              searchParams: {
                item: data._id,
              },
              state: {
                selectedItem: data,
              },
            })
            return
          }
          i += step
        }
      },
      [filteredItems, selectedItem]
    )

    const selectPreviousItem = useCallback(() => {
      selectNeighbour(-1)
    }, [selectNeighbour])

    const selectNextItem = useCallback(() => {
      selectNeighbour(1)
    }, [selectNeighbour])

    const handleOnKeyDown = useCallback(
      (e: KeyboardEvent) => {
        const target = e.target as HTMLElement | null
        if (!target) return
        if (!_.includes(['BODY', 'A'], target.tagName)) return
        switch (e.code) {
          case 'KeyQ':
            selectPreviousItem()
            break
          case 'KeyE':
            selectNextItem()
            break
        }
      },
      [selectPreviousItem, selectNextItem]
    )

    const classNames = useMemo(
      () =>
        _.chain(['mixer sidebar', isCompact ? 'compact' : undefined])
          .compact()
          .join(' ')
          .value(),
      [isCompact]
    )

    const channelsComponent = useMemo(() => {
      return (
        <Channels
          isCompact={isCompact}
          channels={channels}
          config={config}
          itemFilter={itemFilter}
          setItems={setItems}
          patchConfig={patchConfig}
        />
      )
    }, [isCompact, channels, config, itemFilter, patchConfig])

    const toggleNestedEpisodes = useCallback(
      (checked: boolean) => {
        patchConfig({nestedEpisodes: checked})
        if (checked && !selectedSeries && selectedItem?.type === 'episode') {
          const series = mergedItems.find((item) => item.ident === selectedItem.series_id)
          navigate({
            searchParams,
            state: {
              selectedSeries: series,
            },
          })
        }
      },
      [mergedItems, navigate, searchParams, selectedItem, selectedSeries]
    )

    return (
      <div
        className={classNames}
        ref={measureRef}
      >
        <ExpressionEditorAutoCompleteContext
          language={language}
          suggestions={generateExpressionAutoCompleteSuggestions}
        >
          {isCompact ?
            <div style={{display: 'none'}}>
              {/* TODO: refactor so no hidden component is needed */}
              {channelsComponent}
            </div>
          : undefined}
          <Split
            // expandToMin
            sizes={isCompact ? [0, 100] : [25, 75]}
            minSize={isCompact ? [0, 0] : undefined}
            collapsed={isCompact ? 0 : undefined}
            {...rest}
          >
            <div
              style={{
                overflow: 'auto',
              }}
            >
              {isCompact ? undefined : channelsComponent}
            </div>
            <div className='content'>
              <ToolBar
                actions={
                  <>
                    <Input
                      icon='search'
                      placeholder='Search titles...'
                      value={searchQuery}
                      onChange={(_e, {value}) => setSearchQuery(value)}
                      size='mini'
                      style={{marginRight: '0.5em'}}
                    />
                    {isCompact ? undefined : <MediaListStats items={filteredItems} />}
                    <Button.Group
                      compact
                      size='tiny'
                    >
                      {isCompact ?
                        <Popup
                          // inverted
                          openOnTriggerMouseEnter={false}
                          closeOnTriggerMouseLeave={false}
                          // size='tiny'
                          position='bottom left'
                          trigger={
                            <Button
                              compact
                              size='tiny'
                              className='filter-button'
                            >
                              <Icon name='filter' />
                              {channelKeys.length > 0 ?
                                <div className='label'>{channelKeys.length}</div>
                              : undefined}
                            </Button>
                          }
                          content={channelsComponent}
                        />
                      : undefined}
                      {isCompact ?
                        undefined
                      : <Popup
                          size='tiny'
                          content={`export ${filteredItems.length} items`}
                          position='left center'
                          trigger={
                            <Button
                              icon='save'
                              onClick={() => exportAsFile(filteredItems)}
                            />
                          }
                        />
                      }
                      <SettingsPopup
                        position='top center'
                        content={
                          <>
                            <Button.Group
                              compact
                              size='tiny'
                            >
                              <Button
                                content={<PiListBold />}
                                color={config.mediaListViewMode === 'list' ? 'blue' : undefined}
                                onClick={() => patchConfig({mediaListViewMode: 'list'})}
                              />
                              <Button
                                content={<TbCropPortraitFilled />}
                                color={config.mediaListViewMode === 'portrait' ? 'blue' : undefined}
                                onClick={() => patchConfig({mediaListViewMode: 'portrait'})}
                              />
                              {/* <Button
                                                content={<TbCropLandscapeFilled />}
                                                color={config.mediaListViewMode === 'landscape' ? 'blue' : undefined}
                                                onClick={() => patchConfig({mediaListViewMode: 'landscape'})}
                                            /> */}
                            </Button.Group>
                            <Checkbox
                              label='compact mode'
                              checked={isCompact}
                              onChange={(_e, {checked}) => setIsCompact(checked || false)}
                            />
                            <Checkbox
                              label='keep selection in view'
                              checked={config.keepSelectionInView}
                              onChange={(_e, {checked}) =>
                                patchConfig({keepSelectionInView: checked || false})
                              }
                            />
                            <Checkbox
                              label='advanced channel mode'
                              checked={config.showAdvancedControls}
                              onChange={(_e, {checked}) =>
                                patchConfig({showAdvancedControls: checked || false})
                              }
                            />
                            <Checkbox
                              label='nest episodes'
                              checked={config.nestedEpisodes}
                              onChange={(_e, {checked}) => toggleNestedEpisodes(checked || false)}
                            />
                          </>
                        }
                      />
                    </Button.Group>
                  </>
                }
              />
              <div className='viewer'>
                {children}
                <MediaList
                  listRef={mediaListRef}
                  items={filteredItems}
                  viewMode={config.mediaListViewMode === 'list' ? 'list' : 'grid'}
                  itemAspectRatio={config.mediaListViewMode === 'landscape' ? 16 / 9 : 8 / 12}
                  idealColumnWidth={config.mediaListViewMode === 'landscape' ? 300 : 150}
                  onSizeChanged={handleOnMediaListSizeChanged}
                  itemProps={(item, index) => {
                    if (!item) return {}
                    return {
                      overlay: (
                        <>
                          {isCompact ?
                            undefined
                          : <div
                              className='corner bl'
                              style={{
                                fontWeight: 'bold',
                              }}
                            >
                              {index + 1}
                            </div>
                          }
                          {!isCompact && item.sortScore !== undefined ?
                            <div
                              className='corner br'
                              style={
                                (item.sortScore || 0) <= 0 ?
                                  {
                                    color: 'red',
                                    background: 'rgba(100, 0, 0, 0.8)',
                                    fontWeight: 'bold',
                                    textShadow: '1px 1px rgba(0, 0, 0, 0.8)',
                                  }
                                : {
                                    color: 'greenyellow',
                                    background: 'rgba(0, 100, 0, 0.8)',
                                    fontWeight: 'bold',
                                    textShadow: '1px 1px rgba(0, 0, 0, 0.8)',
                                  }
                              }
                            >
                              {item.sortScore.toFixed(3)}
                            </div>
                          : null}
                          {itemOverlay ? itemOverlay(item) : undefined}
                        </>
                      ),
                      parentTitle:
                        selectedSeries?.title && config.nestedEpisodes ?
                          selectedSeries.title
                        : undefined,
                      title:
                        item.type === 'series' && item.ident ?
                          `${item.title} (${mergedItemsWithSeries.episodesBySeries[item.ident]?.length || 0})`
                        : item.title,
                      className:
                        selectedItem && selectedItem._id === item._id ? 'selected' : undefined,
                      onClick: disableOnItemClick ? () => {} : () => handleOnMediaClick(item),
                    }
                  }}
                />
              </div>
            </div>
          </Split>
        </ExpressionEditorAutoCompleteContext>
      </div>
    )
  }
)
